/**
 * /ai/review — prioritized human review queue.
 * Server-side prioritization (docaiReview.queue → pipeline.reviewNext):
 * extraction errors and validation failures first, then auto-accepted
 * documents introducing categorical combinations no golden covers yet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  Text,
  Button,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Select,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { callDocai } from '../../hooks/useDocAI';
import StatusChip from '../../components/AI/StatusChip';

export default function AIReviewQueuePage() {
  const [picks, setPicks] = useState([]);
  const [candidates, setCandidates] = useState(0);
  const [count, setCount] = useState(10);
  const [skills, setSkills] = useState([]);
  const [skillName, setSkillName] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/review$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    callDocai('docaiSkills', { action: 'list' })
      .then((res) => setSkills((res.skills || []).filter((s) => !s.name.startsWith('_'))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiReview', {
        action: 'queue',
        skill: skillName || undefined,
        count,
      });
      setPicks(res.picks || []);
      setCandidates(res.candidates || 0);
    } catch (err) {
      toast({ title: 'Queue load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [skillName, count, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4} flexWrap="wrap">
        <Heading size="md">Review Queue</Heading>
        <HStack>
          <Select size="sm" w="240px" value={skillName} onChange={(e) => setSkillName(e.target.value)} placeholder="Default skill">
            {skills.map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </Select>
          <Select size="sm" w="90px" value={count} onChange={(e) => setCount(Number(e.target.value))}>
            {[5, 10, 20, 50].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Select>
          <Button size="sm" leftIcon={<FiRefreshCw />} onClick={load} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      <Text fontSize="sm" color="gray.500" mb={4}>
        {candidates} candidate document{candidates === 1 ? '' : 's'} not yet golden/promoted. Failures first, then
        documents whose categorical combination (program / purpose / occupancy / …) no golden covers yet.
      </Text>

      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" overflow="hidden">
        {loading ? (
          <Center py={16}><Spinner color="blue.400" /></Center>
        ) : (
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Document</Th>
                <Th>Status</Th>
                <Th>Fails</Th>
                <Th>Warns</Th>
                <Th>Why review this</Th>
              </Tr>
            </Thead>
            <Tbody>
              {picks.map((p) => (
                <Tr
                  key={p.documentId}
                  _hover={{ bg: cardBorder, cursor: 'pointer' }}
                  onClick={() => history.push(`${sectionUrl}/documents/${p.documentId}`)}
                >
                  <Td maxW="380px">
                    <Text fontSize="xs" fontFamily="mono" noOfLines={1}>{p.pdf}</Text>
                  </Td>
                  <Td><StatusChip status={p.status} /></Td>
                  <Td fontSize="xs" color={p.failCount ? 'red.400' : undefined}>{p.failCount}</Td>
                  <Td fontSize="xs" color={p.warnCount ? 'yellow.500' : undefined}>{p.warnCount}</Td>
                  <Td maxW="420px">
                    <Tag size="sm" variant="subtle" colorScheme={p.reason?.startsWith('new combination') ? 'cyan' : 'orange'}>
                      <Text fontSize="10px" noOfLines={2}>{p.reason}</Text>
                    </Tag>
                  </Td>
                </Tr>
              ))}
              {!picks.length && (
                <Tr>
                  <Td colSpan={5}>
                    <Center py={10}>
                      <Text fontSize="sm" color="gray.500">Queue is clear — nothing needs review.</Text>
                    </Center>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
